import React, { useEffect, useState, FormEvent, useCallback } from 'react';
import { useTranslation } from '../i18n';
import { OwnerUnavailablePeriod, fetchOwnerUnavailablePeriods, createOwnerUnavailablePeriod, deleteOwnerUnavailablePeriod } from '../availability';
import { getErrorMessage } from '../types';
import { formatDateOnly } from '../formatDate';

interface Props {
  setError: (s: string) => void;
}

export const OwnerUnavailablePeriods: React.FC<Props> = ({ setError }) => {
  const { t, lang } = useTranslation();
  const [periods, setPeriods] = useState<OwnerUnavailablePeriod[]>([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchOwnerUnavailablePeriods();
      setPeriods(res);
    } catch (err) {
      setError(getErrorMessage(err, t('manage.unavailable_load_error')));
    }
  }, [setError, t]);

  useEffect(() => { void load(); }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(''); setMessageType(null);
    if (!start || !end) { setMessage(t('manage.unavailable_dates_required')); setMessageType('error'); return; }
    if (end < start) { setMessage(t('rental.end_before_start')); setMessageType('error'); return; }
    setSaving(true);
    try {
      const created = await createOwnerUnavailablePeriod(start, end);
      setPeriods(prev => [...prev, created].sort((a,b)=>a.startDate.localeCompare(b.startDate)));
      setStart(''); setEnd('');
      setMessage(t('manage.unavailable_added')); setMessageType('success');
    } catch (err) {
      setMessage(getErrorMessage(err, t('manage.unavailable_add_error'))); setMessageType('error');
    } finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    setMessage(''); setMessageType(null);
    try {
      await deleteOwnerUnavailablePeriod(id);
      setPeriods(prev => prev.filter(p => p.id !== id));
      setMessage(t('manage.unavailable_removed')); setMessageType('success');
    } catch (err) {
      setMessage(getErrorMessage(err, t('manage.unavailable_delete_error'))); setMessageType('error');
    }
  };

  return (
    <div className="card manage-unavailable" style={{ marginBottom: '1rem' }}>
      <h3 className="subsection-title">
        {t('manage.unavailable_title')}{' '}
      </h3>
      {message && (
        <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '0.75rem' }}>
          {message}
          <button type="button" className="dismiss" onClick={() => { setMessage(''); setMessageType(null); }}>&times;</button>
        </div>
      )}
      {periods.length === 0 ? (
        <p className="text-muted">{t('manage.unavailable_empty')}</p>
      ) : (
        <ul className="manage-unavailable__list">
          {periods.map(period => (
            <li key={period.id} className="manage-unavailable__item">
              <span>{formatDateOnly(period.startDate, lang)} – {formatDateOnly(period.endDate, lang)}</span>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(period.id)} disabled={saving}>{t('manage.unavailable_delete')}</button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="manage-unavailable__form">
        <div className="form-row">
          <div className="form-group">
            <label>{t('rental.start_date')}</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} disabled={saving} required />
          </div>
          <div className="form-group">
            <label>{t('rental.end_date')}</label>
            <input type="date" value={end} min={start || undefined} onChange={e => setEnd(e.target.value)} disabled={saving} required />
          </div>
        </div>
        <button type="submit" className="btn btn-secondary btn-sm" disabled={saving}>{saving && <span className="btn-spinner" aria-hidden="true" />}{t('manage.unavailable_add')}</button>
      </form>
    </div>
  );
};

export default OwnerUnavailablePeriods;
